import React from 'react';
import type { AttentionItem } from '../../domain/types';
import type { AttentionItemWorkContextMembership } from '../../domain/radarThreads';
import { slackBallInCourtChip, slackCompetenceChip } from '../../domain/mappers';
import {
  BrandIcon,
  Badge,
  SlackRichText,
  WorkContextChip,
  ViewContextButton,
  ViewDetailButton,
  CardExpandToggle,
} from '../../ui/common';

interface SlackCardHeaderProps {
  item: AttentionItem;
  isExpanded: boolean;
  workContext?: AttentionItemWorkContextMembership;
  onCardClick: () => void;
  onToggleExpanded: (e: React.MouseEvent) => void;
  onNavigateToContext?: () => void;
  onOpenContext?: () => void;
  onOpenDetail?: () => void;
  /** Slack permalink for this thread; the external-open action is omitted
   * (never a dead link) when it isn't available yet. See
   * openspec/changes/provider-item-interaction-ux (Slack lacks an
   * external-open action on normal thread cards). */
  permalink?: string;
}

export const SlackCardHeader: React.FC<SlackCardHeaderProps> = ({
  item,
  isExpanded,
  workContext,
  onCardClick,
  onToggleExpanded,
  onNavigateToContext,
  onOpenContext,
  onOpenDetail,
  permalink,
}) => {
  const ballChip = slackBallInCourtChip(item);
  const competenceChip = slackCompetenceChip(item);
  return (
    <div
      role="button"
      tabIndex={0}
      aria-expanded={isExpanded}
      onClick={onCardClick}
      onKeyDown={(e) => {
        if (e.target !== e.currentTarget) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onCardClick();
        }
      }}
      style={{ cursor: 'pointer' }}
    >
      <div
        style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 8, flexWrap: 'wrap' }}
      >
        <BrandIcon source="slack" size={15} />
        <span
          style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 12,
            fontWeight: 600,
            color: 'var(--z-muted-2)',
          }}
        >
          {item.primary}
        </span>
        <Badge label={item.kind || 'Hilo'} tone="flat" />
        <Badge label={item.secondary} tone={item.st === 'you' ? 'warn' : 'flat'} />
        {ballChip ? <Badge label={ballChip.label} tone={ballChip.tone} /> : null}
        {competenceChip ? (
          <Badge
            label={competenceChip.label}
            tone="accent"
            title={competenceChip.title}
            data-testid={`slack-competence-chip-${item.id}`}
          />
        ) : null}
        {workContext ? (
          <WorkContextChip membership={workContext} onNavigate={onNavigateToContext} />
        ) : null}
        {onOpenContext ? (
          <ViewContextButton
            onClick={(e) => {
              e.stopPropagation();
              onOpenContext();
            }}
          />
        ) : null}
        {onOpenDetail ? (
          <ViewDetailButton
            onClick={(e) => {
              e.stopPropagation();
              onOpenDetail();
            }}
          />
        ) : null}
        {permalink ? (
          <a
            href={permalink}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 5,
              height: 28,
              padding: '0 10px',
              borderRadius: 9,
              border: '1px solid var(--z-border)',
              background: 'var(--z-surface)',
              color: 'var(--z-muted-2)',
              fontSize: 11.5,
              fontWeight: 600,
              textDecoration: 'none',
            }}
          >
            <span>Abrir en Slack</span>
            <span style={{ fontSize: 10 }}>↗</span>
          </a>
        ) : null}
        <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--z-dim)' }}>{item.age}</span>
        <CardExpandToggle
          isExpanded={isExpanded}
          onClick={onToggleExpanded}
          ariaLabel={isExpanded ? 'Contraer hilo' : 'Expandir hilo'}
        />
      </div>

      <h3
        style={{
          margin: '0 0 9px',
          fontSize: 15,
          fontWeight: 700,
          lineHeight: 1.4,
          letterSpacing: '-0.015em',
          color: 'var(--z-text-2)',
          textWrap: 'pretty',
        }}
      >
        <SlackRichText text={item.title} />
      </h3>

      {!isExpanded ? (
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 12 }}>
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="var(--z-accent-3)"
            style={{ flex: '0 0 12px', marginTop: 3 }}
          >
            <path d="M12 2l1.9 6.1L20 10l-6.1 1.9L12 18l-1.9-6.1L4 10l6.1-1.9z" />
          </svg>
          <SlackRichText
            as="p"
            text={item.ai}
            style={{
              margin: 0,
              fontSize: 13,
              lineHeight: 1.55,
              color: 'var(--z-muted-3)',
              textWrap: 'pretty',
            }}
          />
        </div>
      ) : null}
    </div>
  );
};
